// src/contexts/AuthContext.jsx
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { login as apiLogin, logout as apiLogout, fetchMe } from '@/services/apiService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('user');
    if (!saved) return null;
    try {
      const parsed = JSON.parse(saved);
      return parsed ? { ...parsed, user_id: parsed.user_id ?? parsed.id ?? null } : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const persistUser = (u) => {
    if (u) localStorage.setItem('user', JSON.stringify(u));
    else localStorage.removeItem('user');
  };

  // Pull fresh /api/me/ - silent=true prevents overwriting errors set by caller
  const refreshMe = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const me = await fetchMe();

      let normalized;
      if (me?.user) {
        normalized = { ...me.user, profile: me.profile, user_id: me.user.id };
      } else {
        normalized = { ...me, user_id: me.user_id ?? me.id };
      }

      setUser(normalized);
      persistUser(normalized);
      if (!silent) setError(null);
      return normalized;
    } catch (e) {
      // Only set error if not silent - prevents overwriting login errors
      if (!silent) setError(e?.message || 'Failed to load session');
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Try to hydrate session on app mount
  useEffect(() => {
    const access = sessionStorage.getItem('accessToken') || localStorage.getItem('accessToken');
    if (access && !user) {
      refreshMe();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      const token = localStorage.getItem('accessToken');
      if (token) {
        refreshMe(true).catch(() => {
          setUser(null);
          persistUser(null);
          // Same loop guard as the axios interceptor — see apiService.js
          if (!window.location.pathname.startsWith('/login')) {
            window.location.href = '/login?session_expired=true';
          }
        });
      }
    }
  };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refreshMe]);



  const handleLogin = async (username, password) => {
    setLoading(true);
    setError(null);

    try {
      const res = await apiLogin(username, password);
      if (!res?.access) throw new Error('No access token returned');

      // ✅ silent=true so refreshMe doesn't overwrite our error if it fails
      const me = await refreshMe(true);
      if (!me) throw new Error('Could not load user profile');

      return me;
    } catch (err) {
      // ✅ Extract from backend response, never show raw JS errors to user
      const msg =
        err?.response?.data?.errors?.username ||
        err?.response?.data?.errors?.password ||
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        'Invalid credentials. Please check your username and password.';

      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await apiLogout();
    } finally {
      setUser(null);
      persistUser(null);
      window.location.href = '/login';
    }
  };

  const value = {
    user,
    setUser,
    isAuthenticated: !!user,
    loading,
    error,
    handleLogin,
    handleLogout,
    refreshMe,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);